import { useState, Fragment, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLoginResult } from "../State/LoginResultContext";
import { API_URL } from "../utils/constants";
import { showToast } from "../utils/toast";
import "./Login.css";
import { useGoogleLogin } from "@react-oauth/google";
import axios from "axios";
import { FcGoogle } from "react-icons/fc";
import { FaCheck } from "react-icons/fa";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/20/solid";
import PasswordInput from "../Components/PasswordInput";

export default function Login({ switchForm }) {
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordStage, setForgotPasswordStage] = useState("form");
  const [forgotPasswordBusy, setForgotPasswordBusy] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const navigate = useNavigate();

  const resetErrorState = () => {
    setTimeout(() => {
      setMessage("");
      setIsError(false);
    }, 3000);
  };

  useEffect(() => {
    if (message) {
      showToast(message, { type: isError ? "error" : "success" });
    }
  }, [message, isError]);

  const { updateloginResults } = useLoginResult();

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/users/authenticate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
        }),
      });

      const data = await response.json();
      if (response.status === 200) {
        showToast("Signed in successfully.", { type: "success" });
        updateloginResults(data);
        navigate("/my-events");
      } else {
        setMessage(data.message);
        setIsError(true);
        resetErrorState();
      }
    } catch (error) {
      setMessage(error.message);
      setIsError(true);
      resetErrorState();
    }
  };

  const openForgotPasswordModal = () => {
    setForgotPasswordEmail(email || "");
    setForgotPasswordStage("form");
    setForgotPasswordError("");
    setForgotPasswordOpen(true);
  };

  const closeForgotPasswordModal = () => {
    setForgotPasswordOpen(false);
    setTimeout(() => {
      setForgotPasswordStage("form");
      setForgotPasswordBusy(false);
      setForgotPasswordError("");
      setForgotPasswordEmail("");
    }, 150);
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordError("");
    const trimmed = forgotPasswordEmail.trim();

    if (!trimmed) {
      setForgotPasswordError("Please enter your email address");
      return;
    }

    setForgotPasswordBusy(true);
    try {
      const response = await fetch(`${API_URL}/users/sendForgotEmail`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: trimmed,
        }),
      });

      const data = await response.json();
      if (response.status === 200 || response.status === 201) {
        setForgotPasswordStage("success");
        showToast("Check your email for reset instructions.", {
          type: "success",
        });
      } else {
        const errMsg =
          data.message ||
          "Failed to send password reset email. Please try again.";
        setForgotPasswordError(errMsg);
        showToast(errMsg, { type: "error" });
      }
    } catch (error) {
      const errMsg = "Failed to send password reset email. Please try again.";
      setForgotPasswordError(errMsg);
      showToast(errMsg, { type: "error" });
    } finally {
      setForgotPasswordBusy(false);
    }
  };

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      const userInfo = await axios.get(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        },
      );
      //  loin the user
      const response = await fetch(`${API_URL}/users/signInWithOauthProvider`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: userInfo.data.email,
          provider: "google",
          uid: userInfo.data.sub,
        }),
      });

      const data = await response.json();
      if (response.status === 200) {
        showToast("Signed in successfully.", { type: "success" });
        updateloginResults(data);
        navigate("/my-events");
      } else {
        // register the user
        const response = await fetch(
          `${API_URL}/users/signUpWithOauthProvider`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              email: userInfo.data.email,
              provider: "google",
              uid: userInfo.data.sub,
              fullName: userInfo.data.name,
              organization: `Default Organization-${new Date().getTime()}`,
            }),
          },
        );
        const data = await response.json();
        if (response.status === 200) {
          showToast("Account created. Signed in successfully.", {
            type: "success",
          });
          updateloginResults(data);
          navigate("/my-events");
        } else {
          setMessage(data.message);
          setIsError(true);
          resetErrorState();
        }
      }
    },
    onError: () => {
      showToast("Sign in with Google failed.", { type: "error" });
    },
  });

  return (
    <div className="min-h-full flex-col bg-gradient-custom">
      <div className="flex min-h-dvh flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm flex flex-col items-center gap-2 sm:gap-3">
          <img
            src="logo192.png"
            alt="Seymour Logo"
            className="mx-auto h-11 sm:h-20 w-auto rounded-full"
          />
          <h1 className="text-center text-4xl sm:text-5xl font-extrabold text-white atkinson-font">
            Seymour Events
          </h1>
          <h2 className="text-center text-lg font-extrabold text-white">
            Real-time Captions and Translation
          </h2>
          <p className="mt-6 sm:mt-8 text-center text-white atkinson-font w-full">
            Sign into access your account.{" "}
            <span
              className="border-b cursor-pointer hover:text-indigo-500"
              onClick={() => {
                switchForm("register");
              }}
            >
              Create an account.
            </span>
          </p>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-xl">
          <form onSubmit={handleLogin} method="POST" className="space-y-6 mx-4">
            <div>
              <label
                htmlFor="email"
                className="block text-2xl font-bold text-white text-left atkinson-font"
              >
                Email address
              </label>
              <div className="mt-2">
                <input
                  id="email"
                  name="email"
                  type="email"
                  onChange={(e) => setEmail(e.target.value)}
                  value={email}
                  required
                  autoComplete="email"
                  placeholder="Enter your email address"
                  className="block w-full rounded-md border border-[#FFFFFFCC] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm placeholder:text-[#FFFFFFCC] placeholder:text-gray-400 focus:border-[#FFFFFF]"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label
                  htmlFor="password"
                  className="block text-2xl font-bold text-white text-left atkinson-font"
                >
                  Password
                </label>
                <div className="text-sm">
                  <span
                    onClick={openForgotPasswordModal}
                    className="font-semibold text-white/80 hover:text-white border-b border-white/80 hover:border-white cursor-pointer"
                  >
                    Forgot password?
                  </span>
                </div>
              </div>
              <div className="mt-2">
                <PasswordInput
                  id="password"
                  name="password"
                  required
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  value={password}
                  placeholder="Enter your password"
                  className="block w-full rounded-md border border-[#FFFFFFCC] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm placeholder:text-[#FFFFFFCC] placeholder:text-gray-400 focus:border-[#FFFFFF]"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="flex w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                style={{ border: "2px solid #6A6680" }}
              >
                Login
              </button>
            </div>

            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-white atkinson-font cursor-pointer"
                onClick={() => setRememberMe(!rememberMe)}
              >
                Remember Me
              </label>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1 border-t border-2 border-white"></div>
              <span className="text-sm text-white atkinson-font">OR</span>
              <div className="flex-1 border-t border-2 border-white"></div>
            </div>

            <div>
              <button
                type="button"
                onClick={() => googleLogin()}
                className="w-full rounded-lg text-center inline-flex items-center justify-center gap-4 g-sign-btn py-1.5 px-3"
              >
                <FcGoogle />
                <p className="atkinson-font text-[#0000008A] text-base">
                  Sign In with Google
                </p>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <Transition appear show={forgotPasswordOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={closeForgotPasswordModal}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="min-h-full flex items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95 translate-y-1"
                enterTo="opacity-100 scale-100 translate-y-0"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100 translate-y-0"
                leaveTo="opacity-0 scale-95 translate-y-1"
              >
                <Dialog.Panel className="w-full max-w-sm rounded-[20px] border border-[#6A6680] bg-[#230046] shadow-2xl">
                  <div className="p-2">
                    <div className="flex items-start justify-end gap-3">
                      <button
                        type="button"
                        className="!bg-transparent hover:!bg-transparent p-1 text-white hover:text-white"
                        onClick={closeForgotPasswordModal}
                        aria-label="Close"
                      >
                        <XMarkIcon className="h-8 w-8 text-[#6A6680] hover:text-[#FFFFFF]" />
                      </button>
                    </div>

                    {forgotPasswordStage === "form" ? (
                      <div className="py-4">
                        <Dialog.Title className="text-center text-[white] text-[22px] font-bold atkinson-font">
                          Reset Password
                        </Dialog.Title>

                        <form
                          onSubmit={handleForgotPasswordSubmit}
                          className="my-4 space-y-4"
                        >
                          <div>
                            <label className="block text-base font-bold text-white text-left atkinson-font">
                              Email
                            </label>
                            <div className="mt-2">
                              <input
                                value={forgotPasswordEmail}
                                onChange={(e) =>
                                  setForgotPasswordEmail(e.target.value)
                                }
                                type="email"
                                autoComplete="email"
                                required
                                placeholder="Enter your email address"
                                className="block w-full rounded-md border border-[#FFFFFFCC] py-2 px-3 bg-[#595959] text-[#FFFFFF] text-sm placeholder:text-[#FFFFFFCC] placeholder:text-gray-400 focus:border-[#FFFFFF]"
                              />
                            </div>
                          </div>

                          {forgotPasswordError ? (
                            <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-sm text-red-100 atkinson-font">
                              {forgotPasswordError}
                            </div>
                          ) : null}

                          <button
                            type="submit"
                            disabled={forgotPasswordBusy}
                            className="flex !mt-6 w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                            style={{ border: "2px solid #6A6680" }}
                          >
                            {forgotPasswordBusy
                              ? "Sending..."
                              : "Send Reset Link"}
                          </button>
                        </form>
                      </div>
                    ) : (
                      <div className="p-4 pt-0 text-center">
                        <div className="mx-auto flex h-12 w-12 p-3 items-center justify-center rounded-full border border-[#6A6680] bg-white">
                          <FaCheck className="h-10 w-10 text-[#5701AE]" />
                        </div>
                        <Dialog.Title className="mt-4 text-[white] text-[22px] font-bold atkinson-font tracking-tight">
                          Email Sent!
                        </Dialog.Title>
                        <p className="mt-2 text-[#FFFFFF] atkinson-font">
                          A password reset link has been sent to{" "}
                          <span className="font-bold text-white">
                            {forgotPasswordEmail || "[your email]"}
                          </span>
                          . Please check your inbox.
                        </p>

                        <button
                          type="button"
                          className="flex !mt-6 w-full justify-center rounded-md !bg-[#5701AE] p-2 text-base font-semibold text-white hover:!bg-[#7A2FD3]"
                          style={{ border: "2px solid #6A6680" }}
                          onClick={closeForgotPasswordModal}
                        >
                          Done
                        </button>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
